import type { Language, Locale, Region } from '@lemonade-hq/lemonation';
import { DEFAULT_LOCALE } from '@lemonade-hq/lemonation';
import type {
  AnalyticsConfig,
  AuthConfig,
  ContentBundlesPerLocale,
  InfraConfig,
  LayoutConfig,
  LocaleConfig,
  MonitoringConfig,
  NavConfig,
  RealtimeConfig,
  SecurityConfig,
  UserConfig,
} from '@lemonade-hq/nuiza';
import { Env } from '@lemonade-hq/nuiza';
import { getEnvVar } from '@lemonade-hq/ts-helpers';

// WINDOW INTERFACE *******************

declare global {
  interface Window {
    readonly lemonade?: {
      readonly statusCode: number;
      readonly config?: {
        readonly locale?: {
          readonly locale: Locale;
          readonly region: Region;
          readonly lang: Language;
          readonly translations: ContentBundlesPerLocale;
          readonly activeRegions: Region[];
        };
        readonly urls?: {
          readonly monolithUrl?: string;
          readonly consumerUrl?: string;
        };
        readonly user?: {
          readonly userId: string;
          readonly name: string;
          readonly givenName: string;
          readonly familyName: string;
          readonly email: string;
          readonly dateOfBirth?: string;
        };
        readonly csrf: string;
        readonly realtime?: {
          readonly channels: string[];
          readonly pubnubSubscribeKey: string;
        };
      };
      readonly payload?: Record<string, unknown>;
    };
  }
}

const ENV = process.env.ENV as Env;
const MONOLITH_URL = window.lemonade?.config?.urls?.monolithUrl ?? '';

// CONFIG *******************

export function analyticsConfig(): AnalyticsConfig {
  return {
    enableGtm: true,
    gtmId: process.env.GTM_ID,
    segmentKey: process.env.SEGMENT_KEY,
  };
}

export function errorConfig(): { readonly hasError: boolean; readonly statusCode: number } {
  const statusCode = window.lemonade?.statusCode ?? 200;
  const hasError = statusCode >= 400;
  return { hasError, statusCode };
}

export function infraConfig(): InfraConfig {
  return {
    env: getEnvVar<Env>('ENV', Env.Development),
    envId: getEnvVar('ENV_ID', ''),
    service: 'test',
  };
}

export function localeConfig(): LocaleConfig {
  const currentLocale = window.lemonade?.config?.locale?.locale ?? DEFAULT_LOCALE;
  return {
    lang: window.lemonade?.config?.locale?.lang,
    region: window.lemonade?.config?.locale?.region.toUpperCase() as Region,
    activeRegions: window.lemonade?.config?.locale?.activeRegions,
    includeCommonI18nContext: true,
    translations: {
      [currentLocale]: window.lemonade?.config?.locale?.translations,
    },
  };
}

export function layoutConfig(): LayoutConfig {
  return {
    header: {
      hide: true,
      fixed: false,
    },
    footer: {
      hide: true,
    },
    aua: {
      hide: true,
      endpoint: `${MONOLITH_URL}/_mrkt/contact_us`,
    },
  };
}

export function monitoringConfig(): MonitoringConfig {
  const sentryDsn = getEnvVar('CLIENT_SENTRY_DSN', '');

  return {
    sentry: {
      dsn: sentryDsn,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      enabled: ENV !== 'development' && sentryDsn != null && sentryDsn.length > 0,
      environment: ENV,
      release: getEnvVar('RELEASE', ''),
    },
  };
}

export function navConfig(): NavConfig {
  return {
    monolithUrl: window.lemonade?.config?.urls?.monolithUrl ?? 'https://www.lemonade.com',
    consumerUrl: window.lemonade?.config?.urls?.consumerUrl ?? 'https://me.lemonade.com',
  };
}

export function securityConfig(): SecurityConfig {
  return {
    csrf: window.lemonade?.config?.csrf ?? '',
  };
}

export function realtimeConfig(): RealtimeConfig | undefined {
  return window.lemonade?.config?.realtime;
}

export function userConfig(): UserConfig | undefined {
  const user = window.lemonade?.config?.user;
  if (user == null) return undefined;

  return {
    email: user.email,
    givenName: user.givenName,
    familyName: user.familyName,
    userId: user.userId,
    dateOfBirth: user.dateOfBirth,
  };
}

export function authConfig(): AuthConfig | undefined {
  return {
    loginUrl: `${window.location.origin}/auth/login`,
    logoutUrl: `${window.location.origin}/auth/logout`,
    userInfoUrl: `${window.location.origin}/auth/me`,
    unauthorizedBehavior: 'callback',
  };
}
